import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import CollectionCard from './CollectionCard'
import classes from './DraggableGrid.module.css'

export default function DraggableGrid({
  collections,
  onReorder,
  onIdChange,
  onAddCollection,
  onRemoveCollection,
}) {
  const [activeId, setActiveId] = useState(null)
  const [newId, setNewId] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    setActiveId(null)

    if (over && active.id !== over.id) {
      const oldIndex = collections.findIndex((c) => c.f_id === active.id)
      const newIndex = collections.findIndex((c) => c.f_id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(collections, oldIndex, newIndex)
        onReorder(newOrder)
      }
    }
  }

  const handleDragCancel = () => {
    setActiveId(null)
  }

  const handleAddSubmit = async () => {
    if (!newId.trim()) return
    setIsAdding(true)
    const success = await onAddCollection(newId.trim())
    if (success) {
      setNewId('')
    }
    setIsAdding(false)
  }

  const handleAddKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddSubmit()
    }
  }

  const activeCollection = collections.find((c) => c.f_id === activeId)

  const idCounts = collections.reduce((acc, c) => {
    acc[c.f_id] = (acc[c.f_id] || 0) + 1
    return acc
  }, {})
  const isDuplicate = (fId) => idCounts[fId] > 1

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={collections.map((c) => c.f_id)}
        strategy={rectSortingStrategy}
      >
        <div className={classes.gridWrapper}>
          <div className={classes.gridContainer}>
            {collections.map((collection, index) => (
              <CollectionCard
                key={`${collection.f_id}-${index}`}
                collection={collection}
                index={index}
                onIdChange={onIdChange}
                onRemove={onRemoveCollection}
                isDuplicate={isDuplicate(collection.f_id)}
              />
            ))}
            <div className={classes.addCard}>
              <div className={classes.addIcon}>+</div>
              <input
                type="text"
                value={newId}
                onChange={(e) => setNewId(e.target.value)}
                onKeyDown={handleAddKeyDown}
                className={classes.addInput}
                placeholder="Collection ID"
                disabled={isAdding}
              />
              <button
                className={classes.addButton}
                onClick={handleAddSubmit}
                disabled={isAdding || !newId.trim()}
              >
                {isAdding ? '...' : 'ADD'}
              </button>
            </div>
          </div>
        </div>
      </SortableContext>

      <DragOverlay adjustScale={false}>
        {activeCollection ? (
          <div className={classes.dragOverlayCard}>
            <CollectionCard
              collection={activeCollection}
              index={collections.findIndex((c) => c.f_id === activeId)}
              onIdChange={() => {}}
              onRemove={() => {}}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

DraggableGrid.propTypes = {
  collections: PropTypes.arrayOf(
    PropTypes.shape({
      f_id: PropTypes.string.isRequired,
      name: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string
      }),
      header: PropTypes.shape({
        background: PropTypes.shape({
          url: PropTypes.string
        })
      })
    })
  ).isRequired,
  onReorder: PropTypes.func.isRequired,
  onIdChange: PropTypes.func.isRequired,
  onAddCollection: PropTypes.func.isRequired,
  onRemoveCollection: PropTypes.func.isRequired,
}
